import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CounselingMemoUploadType as UploadType,
  RegistrationStatus
} from '@agent-ds/shared/enums';
import { Certificate, CounselingMemo, IndustryType, JobType, StudentDetail } from '@agent-ds/shared/interfaces';
import { DynamicField, FormMeta } from '@agent-ds/shared/models';
import {
  AuthService,
  DynamicFieldService,
  MasterApiService,
  StudentApiService,
  DialogService,
} from '@agent-ds/shared/services';
import { forkJoin, Subscription } from 'rxjs';
import { UserTeamInjectorProvider } from '@agent-ds/shared/services/api/providers/user-team-injector.provider';
import { STUDENT_INTERVIEW_STATUS } from '@agent-ds/shared/constants';
import * as moment from 'moment';
import { first, tap } from 'rxjs/operators';
import { PopupControlComponent } from '@agent-ds/shared/components/popup-control/popup-control.component';
import { isEqual } from 'underscore';
import * as _ from 'underscore';
import { CounselingMemoUploadDialogComponent } from '../counseling-memo-upload-dialog/counseling-memo-upload-dialog.component';
@Component({
  selector: 'ag-counseling-memo-confirm-dialog',
  templateUrl: './counseling-memo-confirm-dialog.component.html',
  styleUrls: ['./counseling-memo-confirm-dialog.component.scss'],
})
export class CounselingMemoConfirmDialogComponent implements OnInit, OnDestroy {
  constructor(
    public dynamicService: DynamicFieldService,
    private dialogService: DialogService,
    public dialog: DialogRef,
    public config: DialogConfig<{ student: StudentDetail, memoRaw: string; memoParsed: Object; uploadType: UploadType; }>,
    public readonly userTeamInjector: UserTeamInjectorProvider,
    private readonly authService: AuthService,
    private studentService: StudentApiService,
    private masterService: MasterApiService,
  ) {}

  student: StudentDetail;
  memoParsedLineDiff: Record<string, string[]>;
  memoParsed: Record<string, any>;
  registerSituationMemoFlg: boolean = false;
  register2ndMatchFlg: boolean = false;
  uploadType: UploadType;
  UploadType = UploadType;
  memoRaw: string;
  memoRawChunked: string[] = [];
  memoToBeMarkedLines: number[] = [];
  counselingMemoStudentDiffFormConfigFields: DynamicField[];
  currentUserId: number;
  editingRegisterInfo: boolean = false;
  updateWithNullFieldList: Record<string, boolean> = {};

  metadataRegister: FormMeta;
  registerInfo: Record<string, any> = {};

  private fieldSubscription: Subscription;
  private masterDataSubscription: Subscription;

  private readonly DEFAULT_EMPTY_CERTIFICATES_VALUE = {
    name: null,
    gradeName: null,
    code: null,
    gradeCode: null,
    issueYear: null,
    issueMonth: null,
  } as const;

  private readonly DEFAULT_EMPTY_JOB_TYPE_VALUE = {
    code1: null,
    code2: null,
  } as const;

  certificatesMaster: Certificate[] = [];
  jobTypesMaster: JobType[] = [];
  industryTypesMaster: IndustryType[] = [];

  ngOnInit() {
    this.student = this.config.data.student;
    this.memoParsedLineDiff = {};
    this.memoParsed = {};
    this.currentUserId = this.authService.loginUser.id;
    this.uploadType = this.config.data.uploadType;

    // 登録ステータス
    this.registerInfo.registrationStatus = this.uploadType === UploadType.UPDATE
      ? this.student.dynamicData.registrationStatus
      : RegistrationStatus.REGISTERED;

    // 面談ステータス
    this.registerInfo.interviewStatus = this.uploadType === UploadType.UPDATE
      ? this.student.dynamicData.interviewStatus
      : STUDENT_INTERVIEW_STATUS.面談済み;

    // カウンセリング日
    this.registerInfo.interviewDate = this.uploadType === UploadType.UPDATE
      ? this.student.dynamicData.interviewDate
      : moment().toISOString();

    this.registerInfo.userId = this.currentUserId;

    // 初期値を true に変更
    this.register2ndMatchFlg = true;
    this.registerSituationMemoFlg = this.uploadType === UploadType.UPDATE;
    this.memoRaw = this.config.data.memoRaw;
    this.memoRawChunked =
      this.config.data.memoRaw
        ? this.config.data.memoRaw.split('\n')
        : [];

    let isMasterDataSubscriptionCompleted = false;
    let isFieldSubscriptionCompleted = false;
    const onBothSubscriptionsCompleted = () => {
      if (isMasterDataSubscriptionCompleted && isFieldSubscriptionCompleted) {
        // 初期化処理
        for (const [key, data] of Object.entries(this.config.data.memoParsed)) {
          const parsedValue = this.formatMemoParsedForDynamicData(key, data && data.value, this.config.data.memoParsed);
          this.memoParsed[key] = parsedValue;
          if (data && parsedValue) {
            this.memoToBeMarkedLines = [ ...this.memoToBeMarkedLines, ...data.linePos ];
            this.memoParsedLineDiff[key] = data.linePos.map(i => this.memoRawChunked[i]);
          } else {
            this.memoParsedLineDiff[key] = [];
          }
        };
      }
    };
    this.masterDataSubscription = this.initFieldOptionMasterData().subscribe({
      next: () => {},
      complete: () => {
        isMasterDataSubscriptionCompleted = true;
        onBothSubscriptionsCompleted();
      }
    });

    this.fieldSubscription = this.dynamicService.fieldUpdateEvent.pipe(first()).subscribe({
      next: () => {
        this.metadataRegister = {
          disabled: false,
          groups: [
            {
              title: '',
              rows: [
                ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student', 'registrationStatus')),
                ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student', 'interviewStatus')),
                ...this.dynamicService.getFormRows(
                  {
                    label: '担当者',
                    fieldType: 'user',
                    fieldName: 'userId',
                  },
                  null,
                  'three-quarter',
                ),
                ...this.dynamicService.getFormRows(this.dynamicService.getDefinition('student', 'interviewDate')),
              ],
            },
          ],
        };
        this.counselingMemoStudentDiffFormConfigFields = [
          this.dynamicService.getDefinition('student', 'communicationSkill'), //コミュニケーション力
          this.dynamicService.getDefinition('student', 'impressionAndCharm'), //印象・愛嬌
          this.dynamicService.getDefinition('student', 'impressionAndCharmRemarks'), //印象・愛嬌 補足
          this.dynamicService.getDefinition('student', 'activityFinish'), //就活終了時期(選択)
          this.dynamicService.getDefinition('student', 'activityFinishRemarks'), //就活終了時期補足
          this.dynamicService.getDefinition('student', 'desiredTimeToEndJobHunting'), //就活終了希望時期(選択)
          this.dynamicService.getDefinition('student', 'jobHuntingAxis'), //就活軸
          this.dynamicService.getDefinition('student', 'locationChange'), //転勤(選択) 転勤(選択)※2項目あり どちらか
          this.dynamicService.getDefinition('student', 'studentActivityUniversity'), //学生時代の活動内容
          this.dynamicService.getDefinition('student', 'studentActivityParttime1'), //アルバイト
          this.dynamicService.getDefinition('student', 'reasonRepeating'), //留年既卒理由
          this.dynamicService.getDefinition('student', 'requestWorkplacePref'), //希望勤務地
          this.dynamicService.getDefinition('student', 'requestWorkplaceRemarks'), //希望勤務地補足
          this.dynamicService.getDefinition('student', 'requestIndustry1'), //希望業種1
          this.dynamicService.getDefinition('student', 'requestIndustry2'), //希望業種2
          this.dynamicService.getDefinition('student', 'requestIndustry3'), //希望業種3
          this.dynamicService.getDefinition('student', 'requestIndustryRemarks'), //希望業種内容補足
          this.dynamicService.getDefinition('student', 'requestJobType1'), //希望職種1
          this.dynamicService.getDefinition('student', 'requestJobType2'), //希望職種2
          this.dynamicService.getDefinition('student', 'requestJobType3'), //希望職種3
          this.dynamicService.getDefinition('student', 'requestJobTypeRemarks'), //希望職種内容補足
          this.dynamicService.getDefinition('student', 'otherDesired'), //その他希望条件
          this.dynamicService.getDefinition('student', 'notDesired'), //希望しないもの
          this.dynamicService.getDefinition('student', 'certificate1'), //資格1
          this.dynamicService.getDefinition('student', 'certificate2'), //資格2
          this.dynamicService.getDefinition('student', 'certificate3'), //資格3
          this.dynamicService.getDefinition('student', 'certificate4'), //資格4
          this.dynamicService.getDefinition('student', 'certificate5'), //資格5
          this.dynamicService.getDefinition('student', 'otherSkill'), //その他資格
          this.dynamicService.getDefinition('student', 'nationality'), //国籍
          this.dynamicService.getDefinition('student', 'motherLanguage'), //母国語
          this.dynamicService.getDefinition('student', 'japaneseLevel'), //日本語レベル
          this.dynamicService.getDefinition('student', 'risk'), //リスク
          this.dynamicService.getDefinition('student', 'mainTransmission'), //主訴
          this.dynamicService.getDefinition('student', 'currentStatus'), //選考中企業
          this.dynamicService.getDefinition('student', 'initiativesInAcademicAndEtc'), //ゼミ・研究内容
          this.dynamicService.getDefinition('student', 'pgExperience'), //PG経験
          this.dynamicService.getDefinition('student', 'programmingExperienceLanguage'), //PG経験（使用言語）
          this.dynamicService.getDefinition('student', 'programmingExperience'), //PG経験（開発内容）
        ].filter(df => !!df);
      },
      complete: () => {
        isFieldSubscriptionCompleted = true;
        onBothSubscriptionsCompleted();
      }
    });
  }

  ngOnDestroy() {
    if (this.fieldSubscription) {
      this.fieldSubscription.unsubscribe();
      this.fieldSubscription = null;
    }
    if (this.masterDataSubscription) {
      this.masterDataSubscription.unsubscribe();
      this.masterDataSubscription = null;
    }
  }

  public existDiffBetweenStudentDynamicFieldAndMemoParsed(field: DynamicField): boolean {
    let memoParsedData = this.memoParsed[field.fieldName];
    if (this.isEmptyMemoData(memoParsedData, field)) {
      memoParsedData = null;
    }

    let studentdynamicData = this.student.dynamicData[field.fieldName];
    if (this.isEmptyMemoData(studentdynamicData, field)) {
      studentdynamicData = null;
    }

    if (field.fieldName.match(/^certificate\d/)) {
      if(memoParsedData === null) {
        memoParsedData = this.DEFAULT_EMPTY_CERTIFICATES_VALUE;
      }
      if(studentdynamicData === null) {
        studentdynamicData = this.DEFAULT_EMPTY_CERTIFICATES_VALUE;
      }
    }
    if (field.fieldName.match(/^requestJobType\d/)) {
      if(memoParsedData === null) {
        memoParsedData = this.DEFAULT_EMPTY_JOB_TYPE_VALUE;
      }
      if(studentdynamicData === null) {
        studentdynamicData = this.DEFAULT_EMPTY_JOB_TYPE_VALUE;
      }
    }

    return !isEqual(memoParsedData, studentdynamicData);
  }

  public toggleEditingRegisterInfo() {
    this.editingRegisterInfo = !this.editingRegisterInfo;
  }

  public onModelChange() {
    if (this.registerInfo.interviewDate) {
      this.registerInfo.interviewDate = moment(this.registerInfo.interviewDate).toISOString();
    }
  }

  public interviewDateForDisplay() : string {
    return moment(this.registerInfo.interviewDate).format('YYYY-MM-DD');
    // return moment(this.registerInfo.interviewDate).format('YYYY-MM-DD HH:mm:ss');
  };

  public getStudentDynamicFieldCategory(fieldName: string): string {
    const definition = {
      communicationSkill: "希望条件", //"options": [ "1", "2", "3", "4", "5", "6", "7", "8" ]
      impressionAndCharm: "希望条件", //"options": [ "1", "2", "3", "4", "5", "6" ]
      impressionAndCharmRemarks: "", //
      activityFinish: "希望条件", //"options": [ "早めに終わりたい", "こだわらない", "大手が落ちたら決める" ]
      activityFinishRemarks: "希望条件", //
      desiredTimeToEndJobHunting: "希望条件", //"options": [ "なるべく早めに終わりたい", "就活終了時期にこだわりはない", "大手を受けてから決めたい", "長引いてもいいので納得するまで続けたい" ]
      jobHuntingAxis: "希望条件", //
      locationChange: "希望条件", //"options": [ "全国転勤可", "全国転勤不可", "関西勤務のみ" ]
      studentActivityUniversity: "希望条件", //
      studentActivityParttime1: "希望条件", //
      reasonRepeating: "希望条件", //
      requestWorkplacePref: "希望条件", //"options": [ "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県", "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県", "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県", "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県", "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県", "海外"
      requestWorkplaceRemarks: "希望条件", //
      requestIndustry1: "希望条件", //1','IT・通信''11','インターネット・広告・メディア''2','メーカー（機械・電気）''3','メーカー（素材・化学・食品・化粧品・その他）''5','商社''6','医薬品・医療機器・ライフサイエンス・医療系サービス''7','金融''8','建設・プラント・不動産''9','コンサルティング・専門事務所・監査法人・税理士法人・リサーチ''10','人材サービス・アウトソーシング・コールセンター''12','小売''24','外食''13','運輸・物流''14','エネルギー（電力・ガス・石油・新エネルギー）''15','旅行・宿泊・レジャー''16','警備・清掃''17','理容・美容・エステ''18','教育''21','農林水産・鉱業''22','公社・官公庁・学校・研究施設''25','冠婚葬祭''23','その他''99','まだ決めていない'
      requestIndustry2: "希望条件", //
      requestIndustry3: "希望条件", //
      requestIndustryRemarks: "希望条件", //
      requestJobType1: "希望条件", //営業職''営業職（法人向け）(営業職)''営業職（個人向け）(営業職)''企画・管理''企画職(企画・管理)''管理部門（人事・経理・法務・総務など）(企画・管理)''事務・アシスタント''事務・アシスタント職(事務・アシスタント)''技術職（SE・エンジニア）''SE・プログラマー(技術職（SE・エンジニア）)''カスタマーエンジニア(技術職（SE・エンジニア）)''ネットワークエンジニア(技術職（SE・エンジニア）)''システム運用・保守(技術職（SE・エンジニア）)''社内SE(技術職（SE・エンジニア）)''ITコンサルタント(技術職（SE・エンジニア）)''データサイエンティスト・アナリスト(技術職（SE・エンジニア）)''技術職（組み込みソフトウェア）''研究・開発職（組み込みソフトウェア）(技術職（組み込みソフトウェア）)''生産・品質管理職（組み込みソフトウェア）(技術職（組み込みソフトウェア）)''技術職（機械・電気）''研究・開発職（機械・電気）(技術職（機械・電気）)''生産・品質管理職（機械・電気）(技術職（機械・電気）)''技術・専門職（建設・不動産等）''技術・専門職（施工管理職）(技術・専門職（建設・不動産等）)''技術・専門職（建築・土木・設計）(技術・専門職（建設・不動産等）)''技術職（化学・素材・化粧品等）''研究・開発職職（化学・素材・化粧品）(技術職（化学・素材・化粧品等）)''生産・品質管理職（化学・素材・化粧品）(技術職（化学・素材・化粧品等）)''技術職（食品・香料・飼料）''研究・開発職職（食品・香料・飼料）(技術職（食品・香料・飼料）)''生産・品質管理職（食品・香料・飼料）(技術職（食品・香料・飼料）)''医療系専門職''介護系専門職(医療系専門職)''医療系専門職(医療系専門職)''金融系専門職''金融系専門職(金融系専門職)''専門職（コンサル・監査法人等）''コンサルタント・研究員(専門職（コンサル・監査法人等）)''その他専門職(専門職（コンサル・監査法人等）)''販売・サービス職''スーパーバイザー・店長(販売・サービス職)''販売スタッフ(販売・サービス職)''バイヤー・マーチャンダイザー(販売・サービス職)''オペレーター(販売・サービス職)''講師・インストラクター(販売・サービス職)''クリエイター・クリエイティブ職''クリエイティブ職(クリエイター・クリエイティブ職)''公務員・教員・農林水産関連職''公務員・団体職員等(公務員・教員・農林水産関連職)''まだ決めていない'
      requestJobType1Detail: "", //
      requestJobType2: "希望条件", //
      requestJobType2Detail: "", //
      requestJobType3: "希望条件", //
      requestJobType3Detail: "", //
      requestJobTypeRemarks: "希望条件", //
      otherDesired: "", //
      notDesired: "", //
      certificate1: "学歴・スキル", //(ISC)2認定資格(CISSP)''「数検」実用数学技能検定(実用数検コース,数学検定コース,算数検定コース)''
      certificate1Grade: "", //
      certificate1IssueYear: "", //
      certificate1IssueMonth: "", //
      certificate2: "学歴・スキル", //
      certificate2Grade: "", //
      certificate2IssueYear: "", //
      certificate2IssueMonth: "", //
      certificate3: "学歴・スキル", //
      certificate3Grade: "", //
      certificate3IssueYear: "", //
      certificate3IssueMonth: "", //
      certificate4: "学歴・スキル", //
      certificate4Grade: "", //
      certificate4IssueYear: "", //
      certificate4IssueMonth: "", //
      certificate5: "学歴・スキル", //
      certificate5Grade: "", //
      certificate5IssueYear: "", //
      certificate5IssueMonth: "", //
      otherSkill: "学歴・スキル", //
      nationality: "詳細", //
      motherLanguage: "詳細", //
      japaneseLevel: "詳細", //"options": [ "ネイティブレベル", "ビジネスレベル", "日常会話レベル", "話せない" ]
      risk: "", //
      mainTransmission: "", //
      currentStatus: "", //
      initiativesInAcademicAndEtc: "希望条件", //
      pgExperience: "希望条件", //"options": [ "1", "2", "3", "4", "5", "6" ]
      programmingExperienceLanguage: "希望条件", //"options": [ "C", "C＋＋", "C#", "HTML", "JAVA", "JAVAScript", "Perl", "PHP", "Python", "Ruby", "Visual Basic", "Go", "ActionScript" ]
      programmingExperience: "希望条件", //
    };
    return definition.hasOwnProperty(fieldName) ? definition[fieldName] : '';
  }

  public close(): void {
    this.dialog.close();
    this.dialogService.open(CounselingMemoUploadDialogComponent, {
      data: {
        student: this.student,
        memoRaw: this.config.data.memoRaw,
        selectedUploadType: this.uploadType
      }
    });
  }

  public update(): void {
    const filteredMemoParsed = Object.entries({
      ...this.memoParsed,
      // Include register data when upload type is 'REGISTER
      ...(this.uploadType === UploadType.REGISTER ? {
        ...this.registerInfo,
        ...(this.registerInfo.interviewDate && {
          interviewDate: moment(this.registerInfo.interviewDate).utc().toISOString()
        })
      } : {})
    }).reduce((acc, [key, value]) => {
      const fieldDefinition = this.counselingMemoStudentDiffFormConfigFields &&
        this.counselingMemoStudentDiffFormConfigFields.find(f => f.fieldName === key);
      if (
        this.updateWithNullFieldList &&
        this.updateWithNullFieldList[key] &&
        this.isEmptyMemoData(value, fieldDefinition)
      ) {
        acc[key] = null;
        return acc;
      }
      // Filters out data that contains 'undefined', 'null', an 'empty string',
      // or when the key contains 'requestJobType#' or 'certificate#' and the
      // value matches its default.
      if (this.isEmptyMemoData(value, fieldDefinition)) return acc;
      acc[key] = value;
      return acc;
    }, {});

    // 初回UL時のみ「カウンセリング内容」に反映
    if(this.uploadType === UploadType.REGISTER) {
      (filteredMemoParsed as any).councelInformation = this.memoRaw;
    }

    const requestPayload: CounselingMemo = {
      memoRaw: this.config.data.memoRaw,
      memoParsed: filteredMemoParsed,
      createSituationMemo: this.registerSituationMemoFlg,
      createSecondMatching: this.register2ndMatchFlg,
      ...(this.config.data.uploadType === UploadType.REGISTER && {
        studentUserId: this.registerInfo.userId,
      })
    };

    PopupControlComponent.instance.open({
      title: '更新',
      content: '以下の内容でMachを更新します。よろしいですか？',
      confirmText: 'OK',
      cancelText: 'キャンセル',
      width: '600px',
      height: '150px',
      confirmCallback: () => {
        if (this.config.data.uploadType === UploadType.REGISTER) {
          this.studentService.processRegisterCounselingMemo(
            this.student.id,
            requestPayload
          ).subscribe((resp) => {
            this.studentService.refreshEvent.emit();
            this.dialog.close();
          });
        } else {
          this.studentService.processUpdateCounselingMemo(
            this.student.id,
            requestPayload
          ).subscribe((resp) => {
            this.studentService.refreshEvent.emit();
            this.dialog.close();
          });
        }
      },
    });
  }

  public onParsedMemoDataChanged(changeEvent:{ value: any}, fieldDefinition: DynamicField): void {
    this.memoParsed[fieldDefinition.fieldName] = changeEvent.value;
  }

  public isEmptyMemoData(value: any, field: DynamicField) {
    if (value == null) return true;

    if (typeof value === 'string') {
      return value.trim() === '';
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (field && field.fieldName && field.fieldName.match(/^certificate\d$/)) {
      if (isEqual(value, this.DEFAULT_EMPTY_CERTIFICATES_VALUE)) return true;
      if (typeof value === 'object') {
        const { name, gradeName, code, gradeCode, issueYear, issueMonth } = value as any;
        return [name, gradeName, code, gradeCode, issueYear, issueMonth].every((item) =>
          item == null || (typeof item === 'string' && item.trim() === '')
        );
      }
    }

    if (field && field.fieldName && field.fieldName.match(/^requestJobType\d$/)) {
      if (isEqual(value, this.DEFAULT_EMPTY_JOB_TYPE_VALUE)) return true;
      if (typeof value === 'object') {
        const { code1, code2 } = value as any;
        return (code1 == null || code1 === '') && (code2 == null || code2 === '');
      }
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }

  private formatMemoParsedForDynamicData(key, value, allMemoParseData) {
    if (key.match(/^certificate\d/)) {
        return this.formatCertificateDynamicDataFromParsedMemoData(key, value, allMemoParseData);
    }

    if (key.match(/^requestIndustry\d$/)) {
        return this.formatIndustryRequestDynamicDataFromParsedMemoData(key, value, allMemoParseData);
    }

    if (key.match(/^requestJobType\d$/)) {
        return this.formatJobRequestDynamicDataFromParsedMemoData(key, value, allMemoParseData);
    }

    const field: DynamicField = this.counselingMemoStudentDiffFormConfigFields.find(f => f.fieldName === key);
    if(
      field &&
      ['list', 'multi-list'].includes(field.fieldType) &&
      field.validationStyle &&
      _.isArray(field.validationStyle.options) &&
      field.validationStyle.options.length > 0
    ) {
      const optionSet = new Set(field.validationStyle.options);
      if (field.fieldType === 'list') {
        return optionSet.has(value) ? value : null;
      }
      if (field.fieldType === 'multi-list') {
        if (!_.isArray(value)) {
          return null;
        }
        let filtered: any[] | null = null;
        for (let i = 0; i < value.length; i++) {
          const v = value[i];
          if (optionSet.has(v)) {
            if (filtered) {
              filtered.push(v);
            }
          } else if (!filtered) {
            filtered = value.slice(0, i);
          }
        }
        return filtered ? (filtered.length > 0 ? filtered : null) : value;
      }
    }

    return value;
  }

  private initFieldOptionMasterData() {
    return forkJoin([
      this.masterService.getCertificates(),
      this.masterService.getJobTypes(),
      this.masterService.getIndustryTypes()
    ]).pipe(
      tap(([certificates, jobTypes, industryTypes]) => {
        this.certificatesMaster = certificates || [];
        this.jobTypesMaster = jobTypes || [];
        this.industryTypesMaster = industryTypes || [];
      })
    );
  }

  private formatCertificateDynamicDataFromParsedMemoData(key: string, value: string, memoParseResponse) {
    // certificate1-5 以外は null を返す
    if (!/^certificate\d$/.test(key)) return null;

    const field: DynamicField = this.counselingMemoStudentDiffFormConfigFields.find(f => f.fieldName === key);
    if(this.isEmptyMemoData(value, field)) {
      return this.DEFAULT_EMPTY_CERTIFICATES_VALUE;
    }

    const matchedCertificate = (this.certificatesMaster || []).find((c) => {
      return String((c as any).name) === String(value);
    });
    if(!matchedCertificate) {
      return this.DEFAULT_EMPTY_CERTIFICATES_VALUE;
    }
    const code = matchedCertificate.code;
    const name = matchedCertificate.name;

    // 等級名
    const certificateGradeValue = memoParseResponse[`${key}Grade`] &&
      memoParseResponse[`${key}Grade`].value;
    let gradeCode;
    let gradeName;
    if(certificateGradeValue) {
      const matchedCertificateGrade = (this.certificatesMaster || []).find(
        (c) =>
          String((c as any).code) === String(code) &&
          String((c as any).gradeName) === String(certificateGradeValue)
      );

      if(matchedCertificateGrade) {
        gradeCode = String(matchedCertificateGrade.gradeCode) || '';
        gradeName = String(matchedCertificateGrade.gradeName) || '';
      }
    }

    // 取得年月
    const certificateIssueYearValue = memoParseResponse[`${key}IssueYear`] &&
      memoParseResponse[`${key}IssueYear`].value;
    const certificateIssueMonthValue = memoParseResponse[`${key}IssueMonth`] &&
      memoParseResponse[`${key}IssueMonth`].value;

    return {
      name: name,
      code: code,
      gradeName: gradeName || null,
      gradeCode: gradeCode || null,
      issueYear: certificateIssueYearValue || "",
      issueMonth: certificateIssueMonthValue || "",
    };
  }

  private formatIndustryRequestDynamicDataFromParsedMemoData(key: string, value: string, memoParseResponse) {
    if(!value) {
      return value;
    }

    const matchedIndustry = (this.industryTypesMaster || []).find((c) => {
      return String((c as any).name) === String(value);
    });

    if(!matchedIndustry) {
      return value;
    }

    return matchedIndustry.code;
  }

  private formatJobRequestDynamicDataFromParsedMemoData(key: string, value: string, memoParseResponse) {
    if (!/^requestJobType\d$/.test(key)) return null;

    const field: DynamicField = this.counselingMemoStudentDiffFormConfigFields.find(f => f.fieldName === key);
    if(this.isEmptyMemoData(value, field)) {
      return this.DEFAULT_EMPTY_JOB_TYPE_VALUE;
    }

    const matchedType = (this.jobTypesMaster || []).find(
      c => String((c as any).name) === String(value)
    );

    if(!matchedType) {
      return this.DEFAULT_EMPTY_JOB_TYPE_VALUE;
    }

    const jobTypeDetails = memoParseResponse[`${key}Detail`] && memoParseResponse[`${key}Detail`].value;
    const matchedTypeDetails = (matchedType.jobTypes || []).find(
      c => String((c as any).name) === String(jobTypeDetails)
    );

    return {
      code1: matchedType.code,
      code2: matchedTypeDetails && matchedTypeDetails.code || null
    };
  }
}
